// apps/merchant-v3/src/lib/guards/MutationGuard.tsx
//
// Phase 6 Wave 12 Task 42 — Platform-wide mutation lockdown when the
// current merchant is suspended. Provides:
//
//   1. <MutationGuardProvider> — context wrapper mounted at AppShell root.
//      Reads `useUser().merchantSuspended` and exposes a stable boolean
//      to consumers.
//   2. useMutationGuard() — hook returning `{ disabled, reason, assert }`.
//      Pages drive the `disabled` prop of mutation buttons + form Submit
//      controls from this. `assert()` throws `MerchantSuspendedError` so
//      programmatic axios calls fail fast before hitting the network.
//   3. <MutationGuard> HOC wrapper — wraps any clickable child in a
//      tooltip + `aria-disabled` shell when suspended; otherwise passes
//      children through unchanged. Useful for one-off CTAs that don't
//      already read the hook.
//   4. assertMutationsAllowed() — module-level helper for non-React code
//      paths (e.g. axios interceptor). Throws when suspended.
//
// Override-reason: mandates that ALL mutations are disabled
// regardless of where the design mockup placed individual CTAs. The
// guard is therefore mounted at AppShell root + is non-overridable —
// the design mockups that show enabled CTAs on the dashboard hero
// (BUDGET-FIX panel, Quick Order chip) are deferred to the lockdown
// behaviour from this provider.
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-114
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useUser } from '@/store/user';
import { t } from '@/lib/i18n';

// --- error type ---------------------------------------------------------

/** Thrown by `assertMutationsAllowed()` when the merchant is suspended.
 *  Caller catches + surfaces a toast / banner; the FE never silently
 *  swallows the error. */
export class MerchantSuspendedError extends Error {
  readonly code = 'MERCHANT_SUSPENDED' as const;
  readonly userMessage: string;

  constructor(userMessage?: string) {
    super(
      userMessage ??
        t('mutationGuard.error', {
          defaultValue:
            'Cannot perform this action while the account is suspended.',
        }),
    );
    this.name = 'MerchantSuspendedError';
    this.userMessage = this.message;
  }
}

// --- module-level snapshot ----------------------------------------------

/**
 * Best-effort module-level snapshot of the suspend flag. Updated by the
 * provider on every render so non-React utilities (axios interceptors,
 * Zustand selectors) can call `assertMutationsAllowed()` without going
 * through React. Kept private — callers should prefer the hook.
 */
let isSuspendedSnapshot = false;

/** Throw `MerchantSuspendedError` when the snapshot says suspended. Safe
 *  to call from any execution context (sync code paths only — does not
 *  await React state). */
export function assertMutationsAllowed(): void {
  if (isSuspendedSnapshot) {
    throw new MerchantSuspendedError();
  }
}

// --- context shape ------------------------------------------------------

export interface MutationGuardValue {
  /** When true, every mutation must be blocked / disabled. */
  disabled: boolean;
  /** Localized reason copy used as `title` / tooltip on disabled controls. */
  reason: string;
  /** Imperative assertion — convenience wrapper around the module-level
   *  `assertMutationsAllowed()`. Throws `MerchantSuspendedError`. */
  assert(): void;
}

const FALLBACK_VALUE: MutationGuardValue = {
  disabled: false,
  reason: '',
  assert(): void {
    /* no-op */
  },
};

const MutationGuardContext = createContext<MutationGuardValue>(FALLBACK_VALUE);

// --- provider -----------------------------------------------------------

interface MutationGuardProviderProps {
  children: ReactNode;
}

export function MutationGuardProvider({
  children,
}: MutationGuardProviderProps): JSX.Element {
  const user = useUser();
  const disabled = user?.merchantSuspended === true;

  // Mirror into module snapshot so non-React utilities can read it.
  isSuspendedSnapshot = disabled;

  const value = useMemo<MutationGuardValue>(
    () => ({
      disabled,
      reason: disabled
        ? t('mutationGuard.disabled', {
            defaultValue: 'Action disabled — account suspended',
          })
        : '',
      assert(): void {
        if (disabled) throw new MerchantSuspendedError();
      },
    }),
    [disabled],
  );

  return (
    <MutationGuardContext.Provider value={value}>
      {children}
    </MutationGuardContext.Provider>
  );
}

// --- hook ---------------------------------------------------------------

/** Returns the current MutationGuard state. Pages should drive button
 *  + form `disabled` props off `useMutationGuard().disabled`. */
export function useMutationGuard(): MutationGuardValue {
  return useContext(MutationGuardContext);
}

// --- HOC wrapper --------------------------------------------------------

interface MutationGuardWrapperProps {
  /** Single React element to wrap. When suspended, the child is cloned
   *  with `disabled={true}` + `aria-disabled` + `title=<reason>` so the
   *  user sees the reason on hover. */
  children: ReactElement;
  /** Force-disable regardless of suspend state — useful when the page
   *  has its own additional precondition. */
  forceDisabled?: boolean;
}

/**
 * Lightweight wrapper that injects `disabled` + `aria-disabled` +
 * `title` on its child when the merchant is suspended. Use it for one-off
 * CTAs that do not (yet) read `useMutationGuard()` directly:
 *
 *   <MutationGuard><Button onClick={...}>Save</Button></MutationGuard>
 *
 * The child MUST be a single React element — string children / fragments
 * are rendered verbatim with no guard.
 */
export function MutationGuard({
  children,
  forceDisabled,
}: MutationGuardWrapperProps): JSX.Element {
  const { disabled, reason } = useMutationGuard();
  const blocked = disabled || forceDisabled === true;

  if (!isValidElement(children)) {
    return <>{children}</>;
  }
  if (!blocked) {
    return children;
  }

  type GuardableProps = {
    disabled?: boolean;
    'aria-disabled'?: boolean;
    title?: string;
    onClick?: (event: unknown) => void;
  };
  const child = children as ReactElement<GuardableProps>;
  const childProps: GuardableProps = child.props ?? {};

  return cloneElement(child, {
    disabled: true,
    'aria-disabled': true,
    title: reason || childProps.title,
    onClick: undefined,
  });
}

export default MutationGuard;
